import React, { createContext, useContext, useEffect, useState } from "react";
import type client from "braintree-web/client";
import type paypal from "braintree-web/paypal";

import { readState } from "@/__main__/app-state.mjs";
import { ensureBraintreeClientToken } from "@/feature-wallet/actions.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const BraintreeClientContext = createContext<client.Client | null>(null);

export function BraintreeClientProvider({
  onReady,
  children,
}: React.PropsWithChildren<{
  onReady?: () => void;
}>) {
  const {
    volatile: { braintreeClientToken },
  } = useSnapshot(readState);

  useEffect(() => {
    if (!braintreeClientToken) {
      ensureBraintreeClientToken();
    }
  }, [braintreeClientToken]);

  const [instance, setInstance] = useState<client.Client | null>(null);

  useEffect(() => {
    if (instance === null) return;

    onReady?.();
  }, [onReady, instance]);

  useEffect(() => {
    if (braintreeClientToken === null || braintreeClientToken === undefined) {
      if (instance !== null) {
        instance.teardown(function () {});
      }

      setInstance(null);
    }

    return () => {
      if (instance !== null) {
        instance.teardown(function () {});
      }
    };
  }, [braintreeClientToken, instance]);

  useEffect(() => {
    if (braintreeClientToken === null || braintreeClientToken === undefined) {
      return;
    }

    let isMounted = true;

    queueMicrotask(async () => {
      if (!isMounted) return;

      const { default: client } = await import("braintree-web/client");

      const newInstance = await client.create({
        authorization: braintreeClientToken,
      });

      if (!isMounted) return;

      setInstance(newInstance);
    });

    return () => {
      isMounted = false;
    };
  }, [braintreeClientToken]);

  return (
    <BraintreeClientContext.Provider value={instance}>
      {children}
    </BraintreeClientContext.Provider>
  );
}

export function useBraintreeClient() {
  return useContext(BraintreeClientContext);
}

const PayPalClientContext = createContext<paypal.PayPal | null>(null);

export function PayPalClientProvider({
  onReady,
  children,
}: React.PropsWithChildren<{
  onReady?: () => void;
}>) {
  const client = useBraintreeClient();

  const [instance, setInstance] = useState<paypal.PayPal | null>(null);

  useEffect(() => {
    if (instance === null) return;

    onReady?.();
  }, [onReady, instance]);

  useEffect(() => {
    if (client === null || client === undefined) {
      if (instance !== null) {
        instance.teardown();
      }

      setInstance(null);
    }

    return () => {
      if (instance !== null) {
        instance.teardown();
      }
    };
  }, [client, instance]);

  useEffect(() => {
    if (client === null || client === undefined) {
      return;
    }

    let isMounted = true;

    queueMicrotask(async () => {
      if (!isMounted) return;

      const { default: paypal } = await import("braintree-web/paypal");

      const newInstance = await paypal.create({
        client,
      });

      if (!isMounted) return;

      setInstance(newInstance);
    });

    return () => {
      isMounted = false;
    };
  }, [client]);

  return (
    <PayPalClientContext.Provider value={instance}>
      {children}
    </PayPalClientContext.Provider>
  );
}

export function usePayPalClient() {
  return useContext(PayPalClientContext);
}
